import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as adminApi from '../api/adminApi';
import { pickErrorMessage } from '../lib/apiHelpers';
import { formatDate } from '../lib/format';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { Modal, ConfirmDialog } from '../components/Modal';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import ImageUploadField from '../components/ImageUploadField';
import {
  IconPencil,
  IconTrash,
  IconEye,
  IconPlus,
  IconCopy,
  IconExternalLink,
  IconImage,
  IconCheck,
} from '../components/Icons';
import { showToast } from '../lib/toast';

const DEFAULT_LIMIT = 10;

const emptyForm = {
  title: '',
  subtitle: '',
  imageUrl: '',
  linkUrl: '',
  displayOrder: 0,
  isActive: true,
};

export default function Banners() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination & filter state
  const [pagination, setPagination] = useState({ skip: 0, limit: DEFAULT_LIMIT });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'active' | 'inactive'

  // Modals state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // View Image Modal state
  const [viewingBanner, setViewingBanner] = useState(null);
  const [copied, setCopied] = useState(false);

  // Delete state
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Quick toggle busy tracking
  const [toggleBusyId, setToggleBusyId] = useState(null);

  const imageUploadRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        skip: pagination.skip,
        limit: pagination.limit,
      };
      if (statusFilter === 'active') params.isActive = true;
      if (statusFilter === 'inactive') params.isActive = false;
      if (search.trim()) params.search = search.trim();

      const res = await adminApi.listBanners(params);
      const items = res?.items ?? (Array.isArray(res) ? res : []);
      const count = Number(res?.totalCount ?? items.length);

      setRows(items);
      setTotalCount(count);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [pagination.skip, pagination.limit, statusFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  function handleFilterChange(newStatus) {
    setStatusFilter(newStatus);
    setPagination((p) => ({ ...p, skip: 0 }));
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPagination((p) => ({ ...p, skip: 0 }));
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  }

  function openEdit(banner) {
    setEditing(banner);
    setForm({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      imageUrl: banner.imageUrl || '',
      linkUrl: banner.linkUrl || '',
      displayOrder: Number(banner.displayOrder) || 0,
      isActive: banner.isActive !== false,
    });
    setModalOpen(true);
  }

  function openViewImage(banner) {
    setViewingBanner(banner);
    setCopied(false);
  }

  async function handleCopyUrl(url) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      showToast('Image URL copied to clipboard!', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('Failed to copy URL', 'danger');
    }
  }

  async function handleToggleActive(banner) {
    const bannerId = banner.bannerId || banner._id;
    if (!bannerId || toggleBusyId) return;

    setToggleBusyId(bannerId);
    try {
      const nextActive = !banner.isActive;
      await adminApi.updateBanner(bannerId, { isActive: nextActive });
      setRows((prev) =>
        prev.map((r) =>
          (r.bannerId || r._id) === bannerId ? { ...r, isActive: nextActive } : r,
        ),
      );
      showToast(
        nextActive ? 'Banner activated' : 'Banner deactivated',
        'success',
      );
    } catch (e) {
      showToast(pickErrorMessage(e), 'danger');
    } finally {
      setToggleBusyId(null);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let finalImageUrl = form.imageUrl;
      if (imageUploadRef.current) {
        finalImageUrl = await imageUploadRef.current.commit();
      }

      if (!finalImageUrl) {
        throw new Error('Please upload a banner image.');
      }

      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        imageUrl: finalImageUrl,
        linkUrl: form.linkUrl.trim(),
        displayOrder: Number(form.displayOrder) || 0,
        isActive: Boolean(form.isActive),
      };

      if (editing) {
        const id = editing.bannerId || editing._id;
        await adminApi.updateBanner(id, payload);
        showToast('Banner updated successfully.', 'success');
      } else {
        await adminApi.createBanner(payload);
        showToast('Banner created successfully.', 'success');
      }

      setModalOpen(false);
      setForm(emptyForm);
      setEditing(null);
      await load();
    } catch (err) {
      showToast(pickErrorMessage(err), 'danger');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await adminApi.deleteBanner(deleteId);
      showToast('Banner deleted successfully.', 'success');
      setDeleteId(null);
      await load();
    } catch (e) {
      showToast(pickErrorMessage(e), 'danger');
    } finally {
      setDeleting(false);
    }
  }

  const columns = [
    {
      key: 'image',
      label: 'Banner Preview',
      width: '190px',
      render: (r) => (
        <div className="d-flex align-items-center gap-2">
          <div
            className="banner-table-thumb-wrap position-relative rounded-3 overflow-hidden border shadow-xs"
            style={{
              width: '125px',
              height: '52px',
              backgroundColor: '#1f242d',
              cursor: 'pointer',
            }}
            onClick={() => openViewImage(r)}
            title="Click to view full image & app preview"
          >
            {r.imageUrl ? (
              <img
                src={r.imageUrl}
                alt={r.title || 'Banner'}
                className="w-100 h-100 object-fit-cover transition-all"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                <IconImage size={20} />
              </div>
            )}
            <div className="banner-table-thumb-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-40 opacity-0 hover-opacity-100 transition-opacity">
              <IconEye size={18} className="text-white" />
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary px-2 py-1 d-inline-flex align-items-center gap-1 shadow-none"
            onClick={() => openViewImage(r)}
            title="View image & mobile preview"
            style={{ fontSize: '0.75rem' }}
          >
            <IconEye size={13} />
            <span>View</span>
          </button>
        </div>
      ),
    },
    {
      key: 'title',
      label: 'Title & Subtitle',
      render: (r) => (
        <div className="min-w-0" style={{ maxWidth: '280px' }}>
          <div className="fw-semibold text-dark text-truncate" title={r.title || 'Untitled'}>
            {r.title || <span className="text-muted fst-italic">Untitled Banner</span>}
          </div>
          {r.subtitle ? (
            <div className="text-secondary small text-truncate" title={r.subtitle}>
              {r.subtitle}
            </div>
          ) : (
            <div className="text-muted small fst-italic">No subtitle</div>
          )}
        </div>
      ),
    },
    {
      key: 'linkUrl',
      label: 'Target / Link',
      render: (r) =>
        r.linkUrl ? (
          <a
            href={r.linkUrl}
            target="_blank"
            rel="noreferrer"
            className="badge bg-light text-primary text-decoration-none border d-inline-flex align-items-center gap-1 py-1 px-2"
            style={{ maxWidth: '180px' }}
            title={r.linkUrl}
          >
            <span className="text-truncate">{r.linkUrl}</span>
            <IconExternalLink size={12} className="flex-shrink-0" />
          </a>
        ) : (
          <span className="text-muted small">—</span>
        ),
    },
    {
      key: 'displayOrder',
      label: 'Order',
      width: '80px',
      render: (r) => (
        <span className="badge bg-secondary-subtle text-secondary-emphasis fw-medium px-2 py-1">
          #{r.displayOrder ?? 0}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (r) => {
        const id = r.bannerId || r._id;
        const isBusy = toggleBusyId === id;
        return (
          <div className="d-flex align-items-center gap-2">
            <div className="form-check form-switch admin-category-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={r.isActive !== false}
                disabled={isBusy}
                onChange={() => handleToggleActive(r)}
                style={{ cursor: isBusy ? 'wait' : 'pointer' }}
                aria-label={`Toggle active state for banner ${r.title || id}`}
              />
            </div>
            <span
              className={`badge ${
                r.isActive !== false
                  ? 'bg-success-subtle text-success border border-success-subtle'
                  : 'bg-secondary-subtle text-secondary border border-secondary-subtle'
              }`}
            >
              {r.isActive !== false ? 'Active' : 'Inactive'}
            </span>
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      label: 'Created',
      width: '140px',
      render: (r) => (
        <span className="small text-secondary">{formatDate(r.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '100px',
      render: (r) => (
        <div className="d-flex justify-content-end gap-1">
          <button
            type="button"
            className="btn btn-sm btn-light border-0 text-secondary p-1"
            onClick={() => openEdit(r)}
            title="Edit banner"
          >
            <IconPencil size={16} />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-light border-0 text-danger p-1"
            onClick={() => setDeleteId(r.bannerId || r._id)}
            title="Delete banner"
          >
            <IconTrash size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Banners"
        subtitle="Manage dynamic promotional banners for home screen"
        actions={
          <button
            type="button"
            className="btn btn-primary d-inline-flex align-items-center gap-2 shadow-sm"
            onClick={openCreate}
          >
            <IconPlus size={16} />
            <span>Add Banner</span>
          </button>
        }
      />

      <ErrorBanner message={error} />

      <Card className="shadow-sm border-0 mb-4">
        {/* Filter and search toolbar */}
        <div className="p-3 border-bottom bg-white rounded-top-3">
          <div className="row g-2 align-items-center justify-content-between">
            <div className="col-12 col-md-6 col-lg-5">
              <form onSubmit={handleSearchSubmit} className="d-flex gap-2">
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search by title, subtitle, or link…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button type="submit" className="btn btn-sm btn-light border px-3">
                  Search
                </button>
              </form>
            </div>

            <div className="col-12 col-md-6 col-lg-6 d-flex justify-content-md-end align-items-center gap-2 flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <label htmlFor="statusFilterSelect" className="small text-secondary mb-0 flex-shrink-0">
                  Status:
                </label>
                <select
                  id="statusFilterSelect"
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
              </div>

              {(search || statusFilter) && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('');
                    setPagination((p) => ({ ...p, skip: 0 }));
                  }}
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Data Table */}
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : rows.length === 0 ? (
            <div className="admin-table-shell admin-table-shell--empty py-5">
              <div className="admin-table-empty text-center py-4">
                <div className="mb-3 text-muted">
                  <IconImage size={48} strokeWidth={1.25} />
                </div>
                <h5 className="fw-semibold text-dark mb-1">No banners found</h5>
                <p className="text-secondary small mb-3">
                  {search || statusFilter
                    ? 'No banners matched your current filter criteria.'
                    : 'Get started by creating your first promotional banner for the customer app.'}
                </p>
                <button
                  type="button"
                  className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                  onClick={openCreate}
                >
                  <IconPlus size={16} />
                  <span>Add First Banner</span>
                </button>
              </div>
            </div>
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              emptyText="No banners found."
              rowKey={(r) => r.bannerId || r._id}
            />
          )}
        </CardBody>

        {/* Offset-based Pagination */}
        <Pagination
          totalCount={totalCount}
          skip={pagination.skip}
          limit={pagination.limit}
          onChange={({ skip, limit }) => setPagination({ skip, limit })}
        />
      </Card>

      {/* CREATE / EDIT MODAL */}
      {modalOpen ? (
        <Modal
          title={editing ? 'Edit Banner' : 'New Promotional Banner'}
          size="lg"
          onClose={() => !saving && setModalOpen(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="bannerForm"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Banner'}
              </button>
            </>
          }
        >
          <form id="bannerForm" onSubmit={handleSave}>
            <div className="row g-3">
              {/* Image Upload with Cropper */}
              <div className="col-12">
                <ImageUploadField
                  ref={imageUploadRef}
                  value={form.imageUrl}
                  onChange={(url) => setForm((f) => ({ ...f, imageUrl: url }))}
                  folder="banner"
                  label="Banner Image"
                  aspect={2.4}
                  outputSize={{ width: 960, height: 400 }}
                  helpText="Standard mobile banner ratio (2.4:1 / 960×400px). Sleek height for home screen. Crop or zoom image. JPG, PNG, WebP up to 5MB."
                />
              </div>

              {/* Title & Subtitle */}
              <div className="col-md-6">
                <label className="form-label fw-semibold small" htmlFor="bannerTitleInput">
                  Title <span className="text-muted fw-normal">(optional)</span>
                </label>
                <input
                  id="bannerTitleInput"
                  type="text"
                  className="form-control"
                  placeholder="e.g. QM Pizza Point"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
                <div className="form-text">Primary bold headline on the banner.</div>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold small" htmlFor="bannerSubtitleInput">
                  Subtitle <span className="text-muted fw-normal">(optional)</span>
                </label>
                <input
                  id="bannerSubtitleInput"
                  type="text"
                  className="form-control"
                  placeholder="e.g. Premium pizza, delivered to your door"
                  value={form.subtitle}
                  onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
                />
                <div className="form-text">Secondary descriptive line under the title.</div>
              </div>

              {/* Action / Target Link */}
              <div className="col-md-8">
                <label className="form-label fw-semibold small" htmlFor="bannerLinkInput">
                  Target Link / Action <span className="text-muted fw-normal">(optional)</span>
                </label>
                <input
                  id="bannerLinkInput"
                  type="text"
                  className="form-control"
                  placeholder="e.g. https://pizzapoint.com/deals or /categories/pizza"
                  value={form.linkUrl}
                  onChange={(e) => setForm((f) => ({ ...f, linkUrl: e.target.value }))}
                />
                <div className="form-text">
                  URL or in-app screen route when customer taps this banner.
                </div>
              </div>

              {/* Display Order */}
              <div className="col-md-4">
                <label className="form-label fw-semibold small" htmlFor="bannerOrderInput">
                  Display Order
                </label>
                <input
                  id="bannerOrderInput"
                  type="number"
                  className="form-control"
                  min={0}
                  step={1}
                  value={form.displayOrder}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, displayOrder: Number(e.target.value) || 0 }))
                  }
                />
                <div className="form-text">Lower number appears first (0, 1, 2…).</div>
              </div>

              {/* Active Toggle Switch */}
              <div className="col-12">
                <div className="p-3 bg-light rounded-3 d-flex align-items-center justify-content-between border">
                  <div>
                    <div className="fw-semibold text-dark small">Active Status</div>
                    <div className="text-muted small">
                      Active banners will be delivered by the API and displayed to customers in the app.
                    </div>
                  </div>
                  <div className="form-check form-switch admin-category-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="bannerActiveSwitch"
                      checked={form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>

              {/* Live Preview Card */}
              {(form.imageUrl || form.title || form.subtitle) && (
                <div className="col-12 mt-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="form-label fw-semibold small text-secondary mb-0">
                      Mobile App Live Preview (Standard Ratio 2.4:1)
                    </label>
                    <span className="badge bg-secondary-subtle text-secondary small">
                      {form.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {/* Simulated App Container */}
                  <div
                    className="p-3 rounded-4 shadow-sm"
                    style={{ backgroundColor: '#121212', border: '1px solid #27272a' }}
                  >
                    <div
                      className="banner-preview-card position-relative rounded-4 overflow-hidden border shadow-sm"
                      style={{
                        aspectRatio: '2.4 / 1',
                        maxHeight: '230px',
                        backgroundColor: '#1c1917',
                        background: form.imageUrl
                          ? `url(${form.imageUrl}) center/cover no-repeat`
                          : 'linear-gradient(135deg, #292524 0%, #1c1917 100%)',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 p-3 p-md-4 d-flex flex-column justify-content-end"
                        style={{
                          background:
                            'linear-gradient(180deg, rgba(0,0,0,0) 25%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.85) 100%)',
                        }}
                      >
                        {form.title ? (
                          <h4
                            className="text-white fw-bold mb-1 fs-5 text-shadow"
                            style={{ letterSpacing: '-0.01em' }}
                          >
                            {form.title}
                          </h4>
                        ) : null}
                        {form.subtitle ? (
                          <p
                            className="small mb-0 text-shadow"
                            style={{ color: 'rgba(255, 255, 255, 0.88)', fontSize: '0.85rem' }}
                          >
                            {form.subtitle}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </Modal>
      ) : null}

      {/* VIEW IMAGE MODAL */}
      {viewingBanner ? (
        <Modal
          title="Banner Preview & Details"
          size="lg"
          onClose={() => setViewingBanner(null)}
          footer={
            <div className="d-flex justify-content-between align-items-center w-100">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                onClick={() => handleCopyUrl(viewingBanner.imageUrl)}
              >
                {copied ? <IconCheck size={16} className="text-success" /> : <IconCopy size={16} />}
                <span>{copied ? 'Copied URL' : 'Copy Image URL'}</span>
              </button>
              <div className="d-flex gap-2">
                {viewingBanner.imageUrl ? (
                  <a
                    href={viewingBanner.imageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                  >
                    <IconExternalLink size={16} />
                    <span>Open in new tab</span>
                  </a>
                ) : null}
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setViewingBanner(null)}
                >
                  Close
                </button>
              </div>
            </div>
          }
        >
          <div className="banner-modal-content">
            {/* Standard Height Banner Display with Mobile Card View */}
            <div
              className="p-3 rounded-4 mb-3"
              style={{ backgroundColor: '#121212', border: '1px solid #27272a' }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2 text-white-50 small">
                <span>Customer App View</span>
                <span className="badge bg-dark border border-secondary text-secondary">Ratio 2.4 : 1 (Sleek Height)</span>
              </div>
              <div
                className="banner-modal-image-wrapper rounded-4 overflow-hidden shadow-sm position-relative"
                style={{
                  aspectRatio: '2.4 / 1',
                  maxHeight: '280px',
                  backgroundColor: '#1c1917',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                }}
              >
                {viewingBanner.imageUrl ? (
                  <>
                    <img
                      src={viewingBanner.imageUrl}
                      alt={viewingBanner.title || 'Banner'}
                      className="w-100 h-100 object-fit-cover"
                    />
                    {(viewingBanner.title || viewingBanner.subtitle) && (
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 p-3 p-md-4 d-flex flex-column justify-content-end"
                        style={{
                          background:
                            'linear-gradient(180deg, rgba(0,0,0,0) 25%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.85) 100%)',
                        }}
                      >
                        {viewingBanner.title && (
                          <h4 className="text-white fw-bold mb-1 fs-5 text-shadow">
                            {viewingBanner.title}
                          </h4>
                        )}
                        {viewingBanner.subtitle && (
                          <p
                            className="small mb-0 text-shadow"
                            style={{ color: 'rgba(255, 255, 255, 0.88)', fontSize: '0.85rem' }}
                          >
                            {viewingBanner.subtitle}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-100 h-100 d-flex align-items-center justify-content-center text-white-50">
                    <IconImage size={48} />
                  </div>
                )}
              </div>
            </div>

            {/* Banner Information Grid */}
            <div className="card border-0 bg-light-subtle rounded-3 p-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.7rem' }}>
                    Title
                  </div>
                  <div className="fw-semibold text-dark">
                    {viewingBanner.title || <span className="text-muted fst-italic">Untitled</span>}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.7rem' }}>
                    Subtitle
                  </div>
                  <div className="text-dark">
                    {viewingBanner.subtitle || <span className="text-muted fst-italic">None</span>}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.7rem' }}>
                    Target / Link URL
                  </div>
                  <div className="text-truncate">
                    {viewingBanner.linkUrl ? (
                      <a
                        href={viewingBanner.linkUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary text-decoration-none d-inline-flex align-items-center gap-1"
                      >
                        <span>{viewingBanner.linkUrl}</span>
                        <IconExternalLink size={13} />
                      </a>
                    ) : (
                      <span className="text-muted fst-italic">No link specified</span>
                    )}
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.7rem' }}>
                    Display Order
                  </div>
                  <div className="fw-semibold text-dark">#{viewingBanner.displayOrder ?? 0}</div>
                </div>

                <div className="col-md-3">
                  <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.7rem' }}>
                    Status
                  </div>
                  <div>
                    <span
                      className={`badge ${
                        viewingBanner.isActive !== false
                          ? 'bg-success-subtle text-success border border-success-subtle'
                          : 'bg-secondary-subtle text-secondary border border-secondary-subtle'
                      }`}
                    >
                      {viewingBanner.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="col-12">
                  <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.7rem' }}>
                    Direct Image URL
                  </div>
                  <div className="p-2 bg-white rounded border small text-break font-monospace text-secondary user-select-all">
                    {viewingBanner.imageUrl || '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* DELETE CONFIRMATION DIALOG */}
      {deleteId ? (
        <ConfirmDialog
          title="Delete Banner"
          message="Are you sure you want to delete this banner? This action cannot be undone."
          confirmLabel="Delete Banner"
          confirmVariant="danger"
          busy={deleting}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteId(null)}
        />
      ) : null}
    </>
  );
}
